import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Calendar, Clock, Package, Tag, Plus, Star } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { useAuth } from '../context/AuthContext';
import { EVENT_PRICING, PACKAGE_DETAILS } from '../utils/pricing';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Dashboard = () => {
  const navigate = useNavigate();
  const { user, isAuthenticated, loading: authLoading } = useAuth();
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showReviewForm, setShowReviewForm] = useState(false);
  const [reviewData, setReviewData] = useState({ rating: 5, comment: '', event_type: '' });
  const [submittingReview, setSubmittingReview] = useState(false);

  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      navigate('/login?redirect=/dashboard');
    }
  }, [authLoading, isAuthenticated, navigate]);

  useEffect(() => {
    const fetchBookings = async () => {
      if (!isAuthenticated) return;
      
      try {
        const response = await axios.get(`${API}/bookings`);
        setBookings(response.data);
      } catch (error) {
        console.error('Error fetching bookings:', error);
        toast.error('Failed to load bookings');
      } finally {
        setLoading(false);
      }
    };

    fetchBookings();
  }, [isAuthenticated]);

  const handleSubmitReview = async (e) => {
    e.preventDefault();
    setSubmittingReview(true);

    try {
      await axios.post(`${API}/reviews`, reviewData);
      toast.success('Thank you for your review!');
      setShowReviewForm(false);
      setReviewData({ rating: 5, comment: '', event_type: '' });
    } catch (error) {
      console.error('Error submitting review:', error);
      toast.error('Failed to submit review');
    } finally {
      setSubmittingReview(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'confirmed': return 'text-green-500 bg-green-500/10';
      case 'pending': return 'text-yellow-500 bg-yellow-500/10';
      case 'cancelled': return 'text-red-500 bg-red-500/10';
      default: return 'text-[#A1A1AA] bg-white/10';
    }
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-[#050505] pt-24 flex items-center justify-center">
        <div className="animate-pulse text-[#A1A1AA]">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505] pt-24 pb-16" data-testid="dashboard-page">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-12">
          <div>
            <h1 className="font-['Playfair_Display'] text-3xl md:text-4xl text-[#EDEDED]">
              Welcome back, <span className="gold-gradient-text">{user?.name}</span>
            </h1>
            <p className="text-[#A1A1AA] mt-2">Manage your bookings and reviews</p>
          </div>
          <div className="flex gap-4">
            <Button
              onClick={() => setShowReviewForm(!showReviewForm)}
              variant="outline"
              className="border-[#D4AF37] text-[#D4AF37]"
              data-testid="write-review-btn"
            >
              <Star className="w-4 h-4 mr-2" />
              Write Review
            </Button>
            <Link to="/book">
              <Button className="gold-gradient-btn" data-testid="new-booking-btn">
                <Plus className="w-4 h-4 mr-2" />
                New Booking
              </Button>
            </Link>
          </div>
        </div>

        {/* Review Form */}
        {showReviewForm && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-[#0a0a0a] rounded-xl p-6 border border-white/10 mb-8"
            data-testid="review-form"
          >
            <h3 className="font-['Playfair_Display'] text-xl text-[#EDEDED] mb-4">Share Your Experience</h3>
            <form onSubmit={handleSubmitReview} className="space-y-4">
              <div>
                <label className="text-sm text-[#A1A1AA] block mb-2">Rating</label>
                <div className="flex gap-2">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <button
                      key={star}
                      type="button"
                      onClick={() => setReviewData(prev => ({ ...prev, rating: star }))}
                      className="transition-transform hover:scale-110"
                      data-testid={`star-${star}`}
                    >
                      <Star
                        className={`w-8 h-8 ${
                          star <= reviewData.rating
                            ? 'text-[#D4AF37] fill-[#D4AF37]'
                            : 'text-[#52525B]'
                        }`}
                      />
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className="text-sm text-[#A1A1AA] block mb-2">Event Type (Optional)</label>
                <select
                  value={reviewData.event_type}
                  onChange={(e) => setReviewData(prev => ({ ...prev, event_type: e.target.value }))}
                  className="w-full bg-[#121212] border border-white/10 rounded-lg p-3 text-[#EDEDED]"
                  data-testid="review-event-type"
                >
                  <option value="">Select event type</option>
                  {Object.entries(EVENT_PRICING).map(([key, event]) => (
                    <option key={key} value={key}>{event.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-sm text-[#A1A1AA] block mb-2">Your Review</label>
                <Textarea
                  value={reviewData.comment}
                  onChange={(e) => setReviewData(prev => ({ ...prev, comment: e.target.value }))}
                  placeholder="Share your experience with Joywin's Clicks..."
                  required
                  rows={4}
                  className="bg-[#121212] border-white/10 text-[#EDEDED] placeholder:text-[#52525B]"
                  data-testid="review-comment"
                />
              </div>
              <div className="flex gap-4">
                <Button
                  type="submit"
                  disabled={submittingReview}
                  className="gold-gradient-btn"
                  data-testid="submit-review-btn"
                >
                  {submittingReview ? 'Submitting...' : 'Submit Review'}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowReviewForm(false)}
                  className="border-white/20 text-[#A1A1AA]"
                >
                  Cancel
                </Button>
              </div>
            </form>
          </motion.div>
        )}

        {/* Saved Discount */}
        {localStorage.getItem('joywin_discountCode') && (
          <div className="bg-gradient-to-r from-[#D4AF37]/20 to-[#D4AF37]/5 rounded-xl p-6 border border-[#D4AF37]/30 mb-8">
            <div className="flex items-center gap-4">
              <Tag className="w-8 h-8 text-[#D4AF37]" />
              <div>
                <p className="text-[#EDEDED] font-medium">You have a saved discount!</p>
                <p className="text-[#D4AF37] font-mono text-lg">
                  {localStorage.getItem('joywin_discountCode')} ({localStorage.getItem('joywin_discount')}% off)
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Bookings */}
        <div>
          <h2 className="font-['Playfair_Display'] text-2xl text-[#EDEDED] mb-6">Your Bookings</h2>
          
          {bookings.length === 0 ? (
            <div className="bg-[#0a0a0a] rounded-xl p-12 border border-white/10 text-center" data-testid="no-bookings">
              <Calendar className="w-16 h-16 text-[#52525B] mx-auto mb-4" />
              <p className="text-[#A1A1AA] mb-4">You don't have any bookings yet</p>
              <Link to="/book">
                <Button className="gold-gradient-btn">
                  Book Your First Session
                </Button>
              </Link>
            </div>
          ) : (
            <div className="space-y-4" data-testid="bookings-list">
              {bookings.map((booking, index) => (
                <motion.div
                  key={booking.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className="bg-[#0a0a0a] rounded-xl p-6 border border-white/10"
                  data-testid={`booking-${index}`}
                >
                  <div className="flex flex-col md:flex-row justify-between gap-4">
                    <div className="space-y-2">
                      <div className="flex items-center gap-3">
                        <h3 className="text-lg font-semibold text-[#EDEDED]">
                          {EVENT_PRICING[booking.event_type]?.name || booking.event_type}
                        </h3>
                        <span className={`px-3 py-1 rounded-full text-xs font-medium capitalize ${getStatusColor(booking.status)}`}>
                          {booking.status}
                        </span>
                      </div>
                      <div className="flex flex-wrap gap-4 text-sm text-[#A1A1AA]">
                        <span className="flex items-center gap-2">
                          <Package className="w-4 h-4" />
                          {PACKAGE_DETAILS[booking.package_tier]?.name || booking.package_tier}
                        </span>
                        <span className="flex items-center gap-2">
                          <Calendar className="w-4 h-4" />
                          {new Date(booking.event_date).toLocaleDateString('en-GB', {
                            day: 'numeric',
                            month: 'long',
                            year: 'numeric'
                          })}
                        </span>
                        <span className="flex items-center gap-2">
                          <Clock className="w-4 h-4" />
                          Booked {new Date(booking.created_at).toLocaleDateString('en-GB')}
                        </span>
                      </div>
                      {booking.additional_services?.length > 0 && (
                        <p className="text-xs text-[#52525B]">
                          Add-ons: {booking.additional_services.join(', ')}
                        </p>
                      )}
                    </div>
                    <div className="text-right">
                      <p className="text-2xl font-['Playfair_Display'] text-[#D4AF37]">
                        £{booking.total_price?.toFixed(2)}
                      </p>
                      {booking.discount_percent > 0 && (
                        <p className="text-xs text-green-500">
                          {booking.discount_percent}% discount applied
                        </p>
                      )}
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
